/**
 * Componente RealtimeIndicator
 * 
 * Indicador visual de dados em tempo real, mostrando status da conexão
 * e última atualização.
 * 
 * @module components/Dashboard/RealtimeIndicator/RealtimeIndicator
 */

import React from 'react';
import PropTypes from 'prop-types';
import { FaCircle, FaWifi, FaTimesCircle } from 'react-icons/fa';
import moment from 'moment';

/**
 * Componente de indicador de tempo real
 * 
 * @param {object} props - Propriedades do componente
 * @param {boolean} props.isConnected - Se está conectado/ativo
 * @param {Date|null} props.lastUpdate - Data da última atualização
 * @param {string} [props.className] - Classes CSS adicionais
 * 
 * @returns {JSX.Element} Componente de indicador
 */
const RealtimeIndicator = ({ isConnected, lastUpdate, className = '' }) => {
    const formatLastUpdate = (date) => {
        if (!date) return 'Nunca';
        
        const now = moment();
        const diff = now.diff(date, 'seconds');
        
        if (diff < 10) return 'Agora';
        if (diff < 60) return `${diff}s atrás`;
        if (diff < 3600) return `${Math.floor(diff / 60)}min atrás`;
        return moment(date).format('HH:mm');
    };

    return (
        <div 
            className={`flex items-center gap-2 ${className}`}
            role="status"
            aria-live="polite"
            aria-label={isConnected ? 'Dados em tempo real ativos' : 'Dados em tempo real inativos'}
        >
            {/* Ícone de status */}
            <div className="relative">
                {isConnected ? (
                    <FaWifi 
                        className="text-emerald-600" 
                        size={14}
                        aria-hidden="true"
                    />
                ) : (
                    <FaTimesCircle 
                        className="text-gray-400" 
                        size={14}
                        aria-hidden="true"
                    />
                )}
                
                {/* Ponto pulsante quando conectado */}
                {isConnected && (
                    <FaCircle 
                        className="absolute -top-1 -right-1 text-emerald-500 animate-pulse" 
                        size={6}
                        aria-hidden="true"
                    />
                )}
            </div>

            {/* Texto de status */}
            <span className={`text-xs font-medium ${
                isConnected ? 'text-emerald-600' : 'text-gray-500'
            }`}>
                {isConnected ? 'Tempo Real' : 'Estático'}
            </span>

            {/* Última atualização */}
            {lastUpdate && (
                <span className="text-xs text-gray-400" aria-label={`Última atualização: ${formatLastUpdate(lastUpdate)}`}>
                    {formatLastUpdate(lastUpdate)}
                </span>
            )}
        </div>
    );
};

RealtimeIndicator.propTypes = {
    /** Se está conectado/ativo */
    isConnected: PropTypes.bool.isRequired,
    
    /** Data da última atualização */
    lastUpdate: PropTypes.instanceOf(Date),
    
    /** Classes CSS adicionais */
    className: PropTypes.string,
};

RealtimeIndicator.defaultProps = {
    lastUpdate: null,
    className: '',
};

export default RealtimeIndicator;
